'use client';

import { useMemo } from 'react';
import * as echarts from 'echarts';
import { Skeleton } from '@mui/material';
import { Inbox } from 'lucide-react';
import DataEmpty from '@/components/DataEmpty';
import useMarketsPriceHistory from '@/hooks/use-markets-price-history';
import { useTranslation } from '@/lib/i18n';
import EChart from '@/components/common/EChart';
import { PolymarketMarket } from '../detail/market/types';

interface GamePriceTrendProps {
  markets: PolymarketMarket[];
  className?: string;
  openInSidebar?: boolean;
}

const CHART_COLORS = ['#A772FF', '#D0D6E4', '#F7931A', '#14C784', '#6288FF'];

export default function GamePriceTrend({ markets, openInSidebar, className }: GamePriceTrendProps) {
  const { t } = useTranslation();

  const historyRequests = useMemo(
    () =>
      markets.map((item) => ({
        conditionId: item.condition_id,
        market: item.clob_tokens_id_yes,
      })),
    [markets],
  );

  const labelByMarket = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of markets) {
      map.set(
        getMarketKey(item.condition_id, item.clob_tokens_id_yes),
        item.group_item_title || item.market_question,
      );
    }
    return map;
  }, [markets]);

  const { data, isLoading } = useMarketsPriceHistory({
    markets: historyRequests,
    enabled: historyRequests.length > 0,
  });

  const series = useMemo<echarts.LineSeriesOption[]>(
    () =>
      data
        .map((item, index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          const name =
            labelByMarket.get(getMarketKey(item.request.conditionId, item.request.market)) ||
            item.request.market;

          return {
            name,
            type: 'line' as const,
            showSymbol: false,
            smooth: true,
            data: item.history
              .filter((point) => Number.isFinite(point.t) && Number.isFinite(point.p))
              .map((point) => [point.t * 1000, point.p * 100] as [number, number]),
            itemStyle: { color },
            lineStyle: { color, width: 1.5 },
            emphasis: { focus: 'series' as const },
          };
        })
        .filter((item) => Array.isArray(item.data) && item.data.length > 0),
    [data, labelByMarket],
  );

  const option = useMemo<echarts.EChartsOption | null>(() => {
    if (series.length === 0) return null;

    return {
      legend: {
        show: true,
        type: 'scroll',
        top: 0,
        left: 0,
        right: 0,
        icon: 'circle',
        itemWidth: 6,
        itemHeight: 6,
        itemGap: 18,
        textStyle: {
          color: '#B7B9BC',
          fontSize: 12,
        },
      },
      grid: {
        left: 8,
        right: 8,
        top: 42,
        bottom: 20,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(18, 18, 18, 0.94)',
        borderColor: '#3A3A3A',
        textStyle: { color: '#fff' },
        valueFormatter: (value: unknown) => formatPercent(Number(value)),
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          color: '#757575',
        },
        axisLine: {
          lineStyle: { color: '#262626' },
        },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        position: 'right',
        axisLabel: {
          color: '#757575',
          formatter: (value: number) => formatPercent(value),
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#262626', type: 'dashed' },
        },
      },
      series,
      animation: true,
      animationDuration: 300,
    };
  }, [series]);

  return (
    <EChart
      option={option}
      loading={isLoading}
      empty={series.length === 0}
      className={className}
      wrapperClassName="mt-4"
      openInSidebar={openInSidebar}
      loadingFallback={
        <Skeleton variant="rounded" width="100%" height={260} sx={{ bgcolor: 'grey.800' }} />
      }
      emptyFallback={
        <div className="absolute inset-0 flex h-65 w-full items-center justify-center rounded-xl border border-[#262626] bg-[#121212]">
          <DataEmpty
            image={<Inbox className="text-[#757575]" />}
            message={t('No data available')}
            classNames={{ wrapper: 'flex-row' }}
          />
        </div>
      }
    />
  );
}

function getMarketKey(conditionId: string, market: string) {
  return `${conditionId}:${market}`;
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(1).replace(/\.0$/, '')}%`;
}
